import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import {
  type GqlCompany,
  type GqlOptionsRegistration,
  type GqlWarrantProgram,
  GqlWarrantProgramKind,
  GqlDocSystemTagKind,
  GqlDocumentSignatureMethod,
} from '../../../../graphql/base-types.graphql';
import { type ComponentProps, useState } from 'react';
import {
  type GqlValidatorStatus,
  useCompanySuspenseQuery,
  useDocGenWarrantRegistrationFormMutation,
  useMeetingHasQesRegFormsSuspenseQuery,
  useOrErrorsLazyQuery,
  useWarrantProgramSuspenseQuery,
} from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { hasValidatorErrors } from '../../utils';
import { apiClient } from '../../../../common/http';
import { isNil, omit } from 'lodash-es';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import {
  useOptionsRegistrationFormSuspenseQuery,
  OptionsRegistrationFormDocument,
} from './OptionsRegistrationForm.graphql';
import { OptionsDocumentsDocument } from '../../../stock-options/common/components/main-steps/options-documents/OptionsDocuments.graphql';
import { Button } from '@startuptools/ui/Button';
import { Trans } from '@lingui/react';
import { Divider } from '@startuptools/ui/Divider';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { useCompanySubscriptionSuspenseQuery } from '../../../../components/side-nav/CompanySubscription.graphql';
import { OptionsRegistrationDocument } from '../../../stock-options/OptionsRegistration.graphql';
import { useNavigate } from '../../../../components/Navigate';

const getDefaultFilename = (
  or: Pick<GqlOptionsRegistration, 'description'>,
  wp: Pick<GqlWarrantProgram, 'warrantKind' | 'name'>,
  company: Pick<GqlCompany, 'name'>,
) => {
  if (wp.warrantKind === GqlWarrantProgramKind.Warrant) {
    return `Registreringsformulär teckningsoptioner - ${company.name} - ${or?.description}`;
  } else if (wp.warrantKind === GqlWarrantProgramKind.Wise) {
    return `Registreringsformulär teckningsoptioner - ${company.name} - ${wp?.name}`;
  }
};

export const OptionsRegistrationForm = ({
  warrantProgramId,
  optionsRegistration,
  navigateToDecisionStep,
}: {
  warrantProgramId: string;
  optionsRegistration: Pick<GqlOptionsRegistration, 'id' | 'description' | 'signatoryId'> & {
    latestRegistrationForm?: DisplayDocument | null;
  };
  navigateToDecisionStep: () => void;
}) => {
  const companyId = useCompanyId();
  const { navigate } = useNavigate();

  const [showStripeDialog, setShowStripeDialog] = useState(false);

  const [fetchOrErrors] = useOrErrorsLazyQuery({
    variables: { companyId, id: optionsRegistration.id },
    fetchPolicy: 'no-cache',
  });

  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { id: warrantProgramId } });
  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });
  const { data: { companySubscription } = { companySubscription: null } } = useCompanySubscriptionSuspenseQuery({
    variables: { companyId },
  });
  const { data: { meetingHasQesRegForms } = { meetingHasQesRegForms: false } } = useMeetingHasQesRegFormsSuspenseQuery({
    variables: {
      companyId,
      input: { id: warrantProgram.meetingConcern?.meetingId ?? '', skipId: optionsRegistration.id },
    },
    skip: !warrantProgram.meetingConcern?.meetingId,
    fetchPolicy: 'no-cache',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doGenerate] = useDocGenWarrantRegistrationFormMutation({
    refetchQueries: [OptionsRegistrationFormDocument, OptionsDocumentsDocument, OptionsRegistrationDocument],
    awaitRefetchQueries: true,
  });
  const defaultFilename = getDefaultFilename(optionsRegistration, warrantProgram, company);

  const companyCanUseProFeatures = companySubscription?.canUseProFeatures ?? false;

  return (
    <>
      <Document
        allowDigitalSignature={companyCanUseProFeatures}
        canUseProFeatures={companyCanUseProFeatures}
        document={optionsRegistration?.latestRegistrationForm}
        documentSystemTagKind={GqlDocSystemTagKind.WarrantRegistrationForm}
        hasDigitalRegistrationForms={meetingHasQesRegForms}
        defaultFileName={defaultFilename}
        generate={async signatureMethod => {
          if (isNil(warrantProgram.paidForAt)) {
            setShowStripeDialog(true);
            return;
          }
          return doGenerate({
            variables: {
              companyId,
              optionsRegistrationId: optionsRegistration.id,
              signatureMethod: signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
            },
          });
        }}
        preview={() => apiClient.previewWarrantRegistrationForm(companyId, optionsRegistration.id)}
        validate={async () => {
          const { data: orErrorData } = await fetchOrErrors();
          if (!orErrorData) {
            return { isValid: false, errors: undefined };
          }
          const orErrors = omit(orErrorData.optionsRegistrationErrors, 'todos');

          if (hasValidatorErrors(orErrors)) {
            const withActions = orErrors as Record<string, GqlValidatorStatus & { action?: React.ReactNode }>;
            // @ts-expect-error TS18048
            withActions.decision.action = (
              <Button color="warning" variant="soft" onClick={navigateToDecisionStep}>
                <Trans id="Go to decision" />
              </Button>
            );
            return { isValid: false, errors: Object.values(withActions) };
          }
          return { isValid: true, errors: undefined };
        }}
        signaturesSlot={
          optionsRegistration.latestRegistrationForm?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
          companyCanUseProFeatures && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={optionsRegistration.latestRegistrationForm.id}
                scriveStatus={optionsRegistration.latestRegistrationForm.scriveStatus}
                zignedAgreementStatus={optionsRegistration.latestRegistrationForm.zignedAgreementStatus}
                signAllowedAction={optionsRegistration.latestRegistrationForm.signAllowedAction}
                isQualifiedEsign
              />
            </>
          )
        }
      />

      {showStripeDialog && warrantProgram.meetingConcern?.meetingId && (
        <InnerStripe
          companyId={warrantProgram.companyId}
          meetingId={warrantProgram.meetingConcern?.meetingId}
          onCancel={() => {
            setShowStripeDialog(false);
          }}
          onPaid={() => {
            setShowStripeDialog(false);
            return doGenerate({
              variables: {
                companyId,
                optionsRegistrationId: optionsRegistration.id,
                signatureMethod:
                  optionsRegistration.latestRegistrationForm?.signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
    </>
  );
};

export const OptionsRegistrationFormDocumentNgWrap = ({
  orId,
  ...props
}: Omit<ComponentProps<typeof OptionsRegistrationForm>, 'optionsRegistration'> & { orId: string }) => {
  const {
    data: { optionsRegistration },
  } = useOptionsRegistrationFormSuspenseQuery({ variables: { orId } });
  return <OptionsRegistrationForm optionsRegistration={optionsRegistration} {...props} />;
};
